import * as Sentry from "@sentry/react";
import {
  isPublicReportLocation,
  redactPublicReportSentryEvent,
  redactPublicReportValue,
} from "../shared/public-report-redact.js";

export { redactPublicReportSentryEvent, redactPublicReportValue };

/** Replay records the address bar and network URLs, and its recording hook cannot scrub DOM events. */
export function clientReplaySampleRates(pathname: string): {
  enabled: boolean;
  session: number;
  onError: number;
} {
  if (isPublicReportLocation(pathname)) return { enabled: false, session: 0, onError: 0 };
  return { enabled: true, session: 0.1, onError: 1 };
}

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const rates = clientReplaySampleRates(window.location.pathname);
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...(rates.enabled
        ? [
            Sentry.replayIntegration({
              beforeErrorSampling() {
                return clientReplaySampleRates(window.location.pathname).enabled;
              },
            }),
          ]
        : []),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: rates.session,
    replaysOnErrorSampleRate: rates.onError,
    beforeSend(event) {
      return redactPublicReportSentryEvent(event);
    },
    beforeSendTransaction(event) {
      return redactPublicReportSentryEvent(event);
    },
    beforeBreadcrumb(breadcrumb) {
      return redactPublicReportValue(breadcrumb);
    },
  });
}

/** A session that started elsewhere and then opened a results link should not keep recording it. */
export function stopReplayOnPublicReport(): void {
  const replay = Sentry.getClient()?.getIntegrationByName("Replay") as { stop?: () => void } | undefined;
  if (typeof replay?.stop === "function") void replay.stop();
}

export { Sentry };
